import { Link, useLocation, useNavigate } from "react-router-dom";
import { Search, Tv, Compass, Radio, Bookmark, Shield, LogOut, User as UserIcon, Newspaper, CalendarClock, BookOpen, Megaphone, Menu, X, Zap, Sparkles, Users2 } from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/lib/auth";

export default function Navbar() {
  const loc = useLocation();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, logout } = useAuth() || {};

  const isActive = (p) => loc.pathname === p || (p === "/live" && loc.pathname === "/");

  const submit = (e) => { e.preventDefault(); if (q.trim()) { nav(`/search?q=${encodeURIComponent(q.trim())}`); setMobileOpen(false); } };

  const links = [
    { to: "/live", label: "Live TV", icon: <Tv className="w-4 h-4"/>, testid: "nav-live" },
    { to: "/tv-guide", label: "TV Guide", icon: <CalendarClock className="w-4 h-4"/>, testid: "nav-guide" },
    { to: "/browse", label: "Browse", icon: <Compass className="w-4 h-4"/>, testid: "nav-browse" },
    { to: "/news", label: "News", icon: <Newspaper className="w-4 h-4"/>, testid: "nav-news" },
    { to: "/books", label: "Books", icon: <BookOpen className="w-4 h-4"/>, testid: "nav-books" },
    { to: "/premium", label: "Premium", icon: <Zap className="w-4 h-4"/>, testid: "nav-premium" },
    { to: "/advertise", label: "Advertise", icon: <Megaphone className="w-4 h-4"/>, testid: "nav-advertise" },
  ];

  return (
    <nav className="nav-glass fixed top-0 left-0 right-0 z-50" data-testid="main-navbar">
      <div className="max-w-[1600px] mx-auto flex items-center gap-4 px-4 lg:px-8 h-16">
        <Link to="/" className="flex items-center gap-2 flex-shrink-0" data-testid="nav-logo">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-gradient-to-br from-[#D4AF37] to-[#8B6914] shadow-[0_4px_16px_rgba(212,175,55,0.4)]">
            <Radio className="w-5 h-5 text-[#050507]" strokeWidth={2.5} />
          </div>
          <div className="font-heading text-xl tracking-tight hidden sm:block">
            <span className="text-white">VOT</span><span className="text-[#D4AF37]">Living</span><span className="text-white/60 text-sm">.tv</span>
          </div>
        </Link>

        <div className="hidden lg:flex items-center gap-1">
          {links.map(l => <NavLink key={l.to} {...l} active={isActive(l.to)} />)}
          {user && <NavLink to="/my-list" label="My List" icon={<Bookmark className="w-4 h-4"/>} active={isActive("/my-list")} testid="nav-my-list"/>}
          {user && <NavLink to="/ministry" label="Ministry" icon={<Users2 className="w-4 h-4"/>} active={loc.pathname.startsWith("/ministry")} testid="nav-ministry"/>}
          {user?.owned_channels?.length > 0 && <NavLink to="/my-channel" label="My Channel" icon={<Sparkles className="w-4 h-4"/>} active={loc.pathname.startsWith("/my-channel")} testid="nav-my-channel"/>}
          {user?.role === "admin" && <NavLink to="/admin" label="Admin" icon={<Shield className="w-4 h-4"/>} active={loc.pathname.startsWith("/admin")} testid="nav-admin"/>}
        </div>

        <form onSubmit={submit} className="ml-auto flex-1 max-w-[220px] hidden md:block" data-testid="search-form">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/50" />
            <input type="text" value={q} onChange={e=>setQ(e.target.value)} placeholder="Search..." className="w-full bg-white/5 border border-white/10 rounded-full pl-10 pr-4 py-2 text-sm placeholder:text-white/40 focus:outline-none focus:border-[#D4AF37]/60" data-testid="search-input"/>
          </div>
        </form>

        {user ? (
          <div className="flex items-center gap-2" data-testid="user-menu">
            <div className="hidden md:flex items-center gap-2 text-sm">
              {user.picture ? <img src={user.picture} alt="" className="w-8 h-8 rounded-full border border-white/20"/> : <div className="w-8 h-8 rounded-full bg-[#D4AF37]/20 border border-[#D4AF37]/40 flex items-center justify-center"><UserIcon className="w-4 h-4 text-[#D4AF37]"/></div>}
              <span className="text-white/80 truncate max-w-[100px]">{user.name}</span>
              {user.premium && <span className="text-[9px] font-bold tracking-widest uppercase px-2 py-1 rounded bg-[#D4AF37]/15 text-[#D4AF37]" data-testid="premium-badge">PREMIUM</span>}
            </div>
            <button onClick={logout} className="w-9 h-9 rounded-full btn-ghost flex items-center justify-center" title="Sign out" data-testid="logout-btn"><LogOut className="w-4 h-4"/></button>
          </div>
        ) : (
          <Link to="/login" className="ml-auto md:ml-0 px-4 py-2 rounded-full btn-gold text-sm" data-testid="nav-login">Sign In</Link>
        )}

        <button onClick={()=>setMobileOpen(!mobileOpen)} className="lg:hidden w-9 h-9 rounded-full btn-ghost flex items-center justify-center" data-testid="mobile-menu-toggle">
          {mobileOpen ? <X className="w-4 h-4"/> : <Menu className="w-4 h-4"/>}
        </button>
      </div>

      {mobileOpen && (
        <div className="lg:hidden border-t border-white/10 bg-[#050507]/95 backdrop-blur-xl" data-testid="mobile-menu">
          <div className="p-4 space-y-1">
            {links.map(l => <MobLink key={l.to} {...l} active={isActive(l.to)} onClick={()=>setMobileOpen(false)}/>)}
            {user && <MobLink to="/my-list" label="My List" icon={<Bookmark className="w-4 h-4"/>} active={isActive("/my-list")} testid="mnav-my-list" onClick={()=>setMobileOpen(false)}/>}
            {user?.role === "admin" && <MobLink to="/admin" label="Admin" icon={<Shield className="w-4 h-4"/>} active={loc.pathname.startsWith("/admin")} testid="mnav-admin" onClick={()=>setMobileOpen(false)}/>}
            <form onSubmit={submit} className="pt-2">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/50"/>
                <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search..." className="w-full bg-white/5 border border-white/10 rounded-full pl-10 pr-4 py-2 text-sm placeholder:text-white/40 focus:outline-none focus:border-[#D4AF37]/60"/>
              </div>
            </form>
          </div>
        </div>
      )}
    </nav>
  );
}

function NavLink({ to, label, icon, active, testid }) {
  return (
    <Link to={to} data-testid={testid} className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium transition-colors ${active ? "bg-white/10 text-white" : "text-white/60 hover:text-white hover:bg-white/5"}`}>
      {icon}<span>{label}</span>
    </Link>
  );
}
function MobLink({ to, label, icon, active, testid, onClick }) {
  return (
    <Link to={to} onClick={onClick} data-testid={testid} className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm ${active ? "bg-white/10 text-white" : "text-white/70 hover:bg-white/5"}`}>
      {icon}<span>{label}</span>
    </Link>
  );
}
